/**
 * Platform Analytics Singleton — web
 *
 * Imperative fire-and-forget tracker callable from anywhere: hooks, event
 * handlers, useEffect. Not a React context — no provider needed.
 *
 * Usage:
 *   import analytics from "@/utils/analytics";
 *   void analytics.track({ entityType: EntityType.hub, entityId: hub._id, event: TrackEvent.impression, surface: TrackSurface.hub_page });
 */

import {AnalyticsApi, EntityType, RouteEntryMethod, TrackEvent, TrackSurface} from "@devour/client";
import getConfig from "@/utils/getConfig";

export interface TrackParams {
    entityType: EntityType;
    entityId: string;
    event: TrackEvent;
    surface: TrackSurface;
}

export interface ArrivalParams {
    /** The matched route PATTERN, e.g. "/drop-in/:slug" — groups every visit to that page. */
    route: string;
    /** The resolved path as visited, e.g. "/drop-in/some-creator". */
    path: string;
    entryMethod: RouteEntryMethod;
    /** document.referrer on first paint; omitted for in-app navigations. */
    referrer?: string;
    /**
     * Slot id of a hub/Drop section that scrolled into view. Present makes this a
     * section view rather than a page arrival — same row shape, same table, so one
     * query spans "who landed" and "who scrolled far enough to see this".
     */
    section?: string;
}

/*
 * One UUID per browser session — stitches events into journeys in BigQuery.
 * sessionStorage is cleared when the tab closes, generating a new id next visit.
 */
const sessionId = sessionStorage.getItem("_dp_sid") ?? (() => {
    const id = crypto.randomUUID();
    sessionStorage.setItem("_dp_sid", id);
    return id;
})();

/**
 * Push a GA4 event to the GTM dataLayer (GTM-NJ47M8DN is loaded in index.html).
 * dataLayer is always an array by the time this runs — GTM initializes it before loading.
 * Silent no-op if dataLayer is somehow absent (e.g. ad blocker removed GTM).
 */
function pushToDataLayer(params: TrackParams): void {
    try {
        const dl = (window as any).dataLayer;
        if (!Array.isArray(dl)) return;
        dl.push({
            event: "devourplay_track",
            dp_event: params.event,
            dp_entity_type: params.entityType,
            dp_entity_id: params.entityId,
            dp_surface: params.surface,
            dp_session_id: sessionId,
        });
    } catch {
        // Never block UX
    }
}

async function track(params: TrackParams): Promise<void> {
    // Fire GA4 synchronously via dataLayer — no network dependency
    pushToDataLayer(params);

    try {
        // getConfig() reads the auth token from the Redux store — no arg needed.
        await new AnalyticsApi(getConfig()).createTrack({
            trackPlatformEventRequest: {
                ...params,
                sessionId,
            },
        });
    } catch {
        // Tracking failures are always silent — never block UX.
    }
}

/**
 * Record that the user arrived on a route.
 *
 * Deliberately NOT `track()`: arrivals go to a BigQuery-only endpoint. The
 * `/track` path upserts shared Mongo counter documents, and putting every
 * navigation on every route through those would reproduce the contention
 * incident documented in `track_platform_event.ts`. See `track_route_arrival.ts`.
 *
 * Called by the router wrapper, never by a page — that is what makes coverage
 * automatic instead of something each page has to remember. The one exception is a
 * `section`, which only the surface that renders sections can know about; that goes
 * through `useSectionImpression` so no page hand-rolls an observer either.
 */
async function trackArrival(params: ArrivalParams): Promise<void> {
    try {
        await new AnalyticsApi(getConfig()).createTrackArrival({
            trackRouteArrivalRequest: {
                route: params.route,
                path: params.path,
                entryMethod: params.entryMethod,
                referrer: params.referrer,
                section: params.section,
                sessionId,
            },
        });
    } catch {
        // Tracking failures are always silent — never block UX.
    }
}

const analytics = {track, trackArrival};
export default analytics;
